import { useState, useCallback } from 'react';
import { safeLocalStorage } from '../utils/safeLocalStorage';

/**
 * Custom hook to manage favorite products with localStorage persistence
 * 
 * @param storageKey - The localStorage key to use for persisting favorites
 * @returns Object containing favoriteProducts set and toggleFavorite function
 * 
 * @example
 * ```tsx
 * const { favoriteProducts, toggleFavorite } = useFavoriteProducts('dfp-product-insights-favorites');
 * 
 * // Check if a product is favorited
 * const isFavorite = favoriteProducts.has(productCode);
 * 
 * // Toggle favorite status
 * <IconButton onClick={() => toggleFavorite(productCode)}>
 *   <StarIcon />
 * </IconButton>
 * ```
 */
export function useFavoriteProducts(storageKey: string) {
  // Initialize state from localStorage
  const [favoriteProducts, setFavoriteProducts] = useState<Set<string>>(() => {
    const saved = safeLocalStorage.getItem<string[]>(storageKey, []);
    return saved ? new Set(saved) : new Set();
  });

  /**
   * Toggle a product's favorite status
   * @param productCode - The product code to toggle
   */
  const toggleFavorite = useCallback((productCode: string) => {
    setFavoriteProducts(prev => {
      const newFavorites = new Set(prev);
      
      if (newFavorites.has(productCode)) {
        newFavorites.delete(productCode);
      } else {
        newFavorites.add(productCode);
      }
      
      // Persist to localStorage
      safeLocalStorage.setItem(storageKey, [...newFavorites]);
      
      return newFavorites;
    });
  }, [storageKey]);

  return { favoriteProducts, toggleFavorite };
}

